import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ArrowRight, CheckCircle } from 'lucide-react'

const ServiceTemplate = ({ title, subtitle, description, features, heroImage }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    
    try {
      const leads = JSON.parse(localStorage.getItem('serviceLeads') || '[]')
      const newLead = {
        ...data,
        service: title,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        status: 'new'
      }
      leads.push(newLead)
      localStorage.setItem('serviceLeads', JSON.stringify(leads))
      
      toast.success('Request submitted! Our team will contact you soon.')
      reset()
    } catch (error) {
      toast.error('Failed to submit request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen pt-20" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="1:1" data-component-id="client/src/components/ServiceTemplate.jsx:1:1">
      <section 
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-component-path="client/src/components/ServiceTemplate.jsx"
        data-component-line="2:3"
        data-component-id="client/src/components/ServiceTemplate.jsx:2:3"
      >
        <div className="absolute inset-0 bg-dark-primary/80" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="11:5" data-component-id="client/src/components/ServiceTemplate.jsx:11:5"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="13:5" data-component-id="client/src/components/ServiceTemplate.jsx:13:5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-component-path="client/src/components/ServiceTemplate.jsx"
            data-component-line="14:7"
            data-component-id="client/src/components/ServiceTemplate.jsx:14:7"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="19:9" data-component-id="client/src/components/ServiceTemplate.jsx:19:9">
              <span className="text-gradient" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="20:11" data-component-id="client/src/components/ServiceTemplate.jsx:20:11">{title}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="22:9" data-component-id="client/src/components/ServiceTemplate.jsx:22:9">
              {subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-dark-primary" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="29:3" data-component-id="client/src/components/ServiceTemplate.jsx:29:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="30:5" data-component-id="client/src/components/ServiceTemplate.jsx:30:5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="31:7" data-component-id="client/src/components/ServiceTemplate.jsx:31:7">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              data-component-path="client/src/components/ServiceTemplate.jsx"
              data-component-line="32:9"
              data-component-id="client/src/components/ServiceTemplate.jsx:32:9"
            >
              <h2 className="text-4xl font-bold mb-6" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="37:11" data-component-id="client/src/components/ServiceTemplate.jsx:37:11">Overview</h2>
              <p className="text-gray-400 text-lg leading-relaxed" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="38:11" data-component-id="client/src/components/ServiceTemplate.jsx:38:11">
                {description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              data-component-path="client/src/components/ServiceTemplate.jsx"
              data-component-line="44:9"
              data-component-id="client/src/components/ServiceTemplate.jsx:44:9"
            >
              {features.map((feature, index) => (
                <div key={index} className="glass-panel rounded-xl p-6" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="51:13" data-component-id="client/src/components/ServiceTemplate.jsx:51:13">
                  <div className="w-12 h-12 bg-neon-green/10 rounded-xl flex items-center justify-center mb-4 text-neon-green" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="52:15" data-component-id="client/src/components/ServiceTemplate.jsx:52:15">
                    {feature.icon}
                  </div>
                  <h3 className="font-bold text-white mb-2" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="55:15" data-component-id="client/src/components/ServiceTemplate.jsx:55:15">{feature.title}</h3>
                  <p className="text-gray-400 text-sm" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="56:15" data-component-id="client/src/components/ServiceTemplate.jsx:56:15">{feature.description}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="glass-panel rounded-2xl p-8 md:p-12" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="62:7" data-component-id="client/src/components/ServiceTemplate.jsx:62:7">
            <h2 className="text-3xl font-bold mb-6 text-center" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="63:9" data-component-id="client/src/components/ServiceTemplate.jsx:63:9">Request a Consultation</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto space-y-6" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="64:9" data-component-id="client/src/components/ServiceTemplate.jsx:64:9">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="65:11" data-component-id="client/src/components/ServiceTemplate.jsx:65:11">
                <div data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="66:13" data-component-id="client/src/components/ServiceTemplate.jsx:66:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="67:15" data-component-id="client/src/components/ServiceTemplate.jsx:67:15">Name *</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    data-component-path="client/src/components/ServiceTemplate.jsx"
                    data-component-line="68:15"
                    data-component-id="client/src/components/ServiceTemplate.jsx:68:15"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="74:35" data-component-id="client/src/components/ServiceTemplate.jsx:74:35">{errors.name.message}</p>}
                </div>
                <div data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="76:13" data-component-id="client/src/components/ServiceTemplate.jsx:76:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="77:15" data-component-id="client/src/components/ServiceTemplate.jsx:77:15">Email *</label>
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required' })}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    data-component-path="client/src/components/ServiceTemplate.jsx"
                    data-component-line="78:15"
                    data-component-id="client/src/components/ServiceTemplate.jsx:78:15"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="84:36" data-component-id="client/src/components/ServiceTemplate.jsx:84:36">{errors.email.message}</p>}
                </div>
              </div>
              <div data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="87:11" data-component-id="client/src/components/ServiceTemplate.jsx:87:11">
                <label className="block text-sm font-semibold mb-2" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="88:13" data-component-id="client/src/components/ServiceTemplate.jsx:88:13">Company</label>
                <input
                  type="text"
                  {...register('company')}
                  className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                  data-component-path="client/src/components/ServiceTemplate.jsx"
                  data-component-line="89:13"
                  data-component-id="client/src/components/ServiceTemplate.jsx:89:13"
                />
              </div>
              <div data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="96:11" data-component-id="client/src/components/ServiceTemplate.jsx:96:11">
                <label className="block text-sm font-semibold mb-2" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="97:13" data-component-id="client/src/components/ServiceTemplate.jsx:97:13">Project Details *</label>
                <textarea
                  {...register('details', { required: 'Project details are required' })}
                  rows="4"
                  className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors resize-none"
                  data-component-path="client/src/components/ServiceTemplate.jsx"
                  data-component-line="98:13"
                  data-component-id="client/src/components/ServiceTemplate.jsx:98:13"
                ></textarea>
                {errors.details && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="104:38" data-component-id="client/src/components/ServiceTemplate.jsx:104:38">{errors.details.message}</p>}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 px-8 bg-neon-green text-dark-primary font-bold rounded-xl hover:shadow-lg hover:shadow-neon-green/50 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                data-component-path="client/src/components/ServiceTemplate.jsx"
                data-component-line="106:11"
                data-component-id="client/src/components/ServiceTemplate.jsx:106:11"
              >
                {isSubmitting ? 'Submitting...' : 'Request Consultation'} <ArrowRight className="w-5 h-5" data-component-path="client/src/components/ServiceTemplate.jsx" data-component-line="112:69" data-component-id="client/src/components/ServiceTemplate.jsx:112:69" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ServiceTemplate